import { useEffect, useMemo, useState } from 'react';

import { appendLinksourceToUrl, gtmProxy } from '@/utils/analytics';
import { getCookieValue } from '@/utils/getCookieValue';

const DEFAULT_LINKSOURCE = 'unknown';

/**
 * Custom hook to retrieve and manage the linksource value.
 *
 * @param {string} [url] Optional URL to which the linksource will be appended.
 * @param {boolean} [willUpdate=false] Indicates whether the hook should update its value when the linksource changes.
 * @param {boolean} [withLinksource=false] Specifies whether the linksource should be appended to the provided URL.
 * @returns {{url: string, linksource: string, isLinksourceReady: boolean}} Object containing the updated URL, current linksource value, and readiness status.
 */
export function useLinksource({ url, willUpdate, withLinksource }) {
    // Initialize the linksource state with a default value
    const [linksource, setLinksource] = useState(DEFAULT_LINKSOURCE);
    const [isLinksourceReady, setIsLinksourceReady] = useState(false);
    const isServer = typeof window === 'undefined';

    // On client mount, load the cookie
    useEffect(() => {
        if (isServer) return;
        const cookieValue = getCookieValue('linksource') || DEFAULT_LINKSOURCE;
        setLinksource(cookieValue);
        setIsLinksourceReady(true);
    }, [isServer]);

    // Listen for your GTM event if you need live updates
    useEffect(() => {
        if (!willUpdate || isServer || !gtmProxy) return;
        const handler = () => {
            const updated = getCookieValue('linksource') || DEFAULT_LINKSOURCE;
            setLinksource(updated);
        };
        gtmProxy.addListener('linksource_ready', handler);
        return () => {
            gtmProxy.removeListener('linksource_ready', handler);
        };
    }, [willUpdate, isServer]);

    // Compute href in render, so it’s always in sync with state
    const finalUrl = useMemo(() => {
        return url && withLinksource && linksource !== DEFAULT_LINKSOURCE
            ? appendLinksourceToUrl(url, linksource)
            : url || '';
    }, [url, withLinksource, linksource]);

    return {
        url: finalUrl,
        linksource,
        isLinksourceReady,
    };
}
